/**
 * Validation utilities for user input
 */

/**
 * Validates email addresses using a pragmatic RFC 5322-inspired pattern.
 */
export const isValidEmail = (email: string): boolean => {
  const trimmedEmail = email.trim();
  if (!trimmedEmail) return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(trimmedEmail);
};

/**
 * Validates phone number - accepts international formats (10-15 digits)
 * Only allows digits and common formatting chars: + - ( ) . and spaces
 * Returns true for empty strings (phone is optional)
 */
export const isValidPhone = (phone: string): boolean => {
  if (!phone.trim()) return true; // Empty is valid (optional field)
  // Reject if contains letters or invalid characters
  if (/[a-zA-Z]/.test(phone)) return false;
  // Only allow digits and common phone formatting characters
  if (!/^[\d\s\-+().]+$/.test(phone)) return false;
  const digits = phone.replace(/\D/g, "");
  return digits.length >= 10 && digits.length <= 15;
};

/**
 * Formats a phone number for display
 * - 10 digits: (XXX) XXX-XXXX
 * - 11 digits (US with country code): +1 (XXX) XXX-XXXX
 * - International (12+ digits): +XXX XXX XXX XXXX (spaced groups)
 */
export const formatPhoneNumber = (phone: string): string => {
  if (!phone) return phone;

  const digits = phone.replace(/\D/g, "");

  // US format: 10 digits
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  // US with country code: 11 digits starting with 1
  if (digits.length === 11 && digits.startsWith("1")) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }

  // International numbers: add spacing for readability
  // Group from right: last group 4 digits, then groups of 3, remainder is country code
  if (digits.length > 10) {
    const groups: string[] = [];
    let remaining = digits;

    // Work from the right: last group is 4 digits
    groups.unshift(remaining.slice(-4));
    remaining = remaining.slice(0, -4);

    // Middle groups of 3
    while (remaining.length > 3) {
      groups.unshift(remaining.slice(-3));
      remaining = remaining.slice(0, -3);
    }

    // First group is the country code (whatever remains)
    if (remaining.length > 0) {
      groups.unshift(remaining);
    }

    return `+${groups.join(" ")}`;
  }

  return phone;
};
